import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useBrand, useProductName } from "@/context/BrandContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Phone } from "@phosphor-icons/react";

export default function ResetPassword() {
  const { brand } = useBrand();
  const productName = useProductName();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => (params.get("token") || "").trim(), [params]);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) {
      setError("Missing reset token. Request a new link from the sign-in page.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      navigate("/login", { replace: true, state: { resetOk: true } });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, err) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const logo = brand.logo_url || brand.login_logo_url;

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background" data-testid="reset-password-page">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-3 mb-8">
          {logo ? (
            <img src={logo} alt="" className="h-10 w-10 object-contain" />
          ) : (
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-primary text-primary-foreground">
              <Phone className="h-5 w-5" weight="fill" />
            </div>
          )}
          <p className="font-display font-bold tracking-tight">{productName || "Account"}</p>
        </div>
        <h1 className="text-2xl font-display font-semibold tracking-tight">Choose a new password</h1>
        <p className="text-sm text-muted-foreground mt-1.5">
          Set a new password for your {productName || "account"}.
        </p>
        <form onSubmit={submit} className="mt-8 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="password">New password</Label>
            <Input
              id="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="reset-password-input"
              className="h-11"
              placeholder="At least 8 characters"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm">Confirm password</Label>
            <Input
              id="confirm"
              type="password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="reset-password-confirm"
              className="h-11"
            />
          </div>
          {error && <p className="text-sm text-destructive" data-testid="reset-password-error">{error}</p>}
          <Button type="submit" disabled={loading || !token} data-testid="reset-password-submit" className="w-full h-11 rounded-lg">
            {loading ? "Updating…" : "Update password"}
          </Button>
        </form>
        <p className="text-sm text-muted-foreground mt-6 text-center">
          <Link to="/forgot-password" className="text-primary font-medium hover:underline">Request a new link</Link>
          {" · "}
          <Link to="/login" className="text-primary font-medium hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
